import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Clock, 
  Edit, 
  Trash2, 
  Plus,
  Calendar,
  Play,
  Square,
  Save,
  X,
  Search,
  Filter,
  User,
  Users,
  ChevronDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useIdleDetection } from './IdleDetection';
import { toUTCPlus5, parseApiDate } from '../utils/time';
import { useParams, useNavigate } from 'react-router-dom';
import ConfirmModal from './ConfirmModal';
import FormDrawer from './FormDrawer';
import {
  useTimeEntries,
  useTimeEntryStatistics,
  useProjects,
  useTasks,
  useUsers,
  useCreateTimeEntry,
  useUpdateTimeEntry,
  useDeleteTimeEntry,
  useDailyHours,
  useSystemSettings
} from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import useDebounce from '../hooks/useDebounce';

const ENTRIES_PER_PAGE = 10;

const TimeEntriesPage = () => {
  const { user } = useAuth();
  const { time_entry_id } = useParams();
  const navigate = useNavigate();
  const { data: systemSettings } = useSystemSettings();
  const dailyHoursCap = systemSettings?.daily_hours_cap ?? SYSTEM_SETTINGS_DEFAULTS.daily_hours_cap;
  const minDescLen = systemSettings?.min_description_length ?? SYSTEM_SETTINGS_DEFAULTS.min_description_length;
  const flaggedHours = systemSettings?.flagged_entry_threshold_hours ?? SYSTEM_SETTINGS_DEFAULTS.flagged_entry_threshold_hours;
  
  const [filters, setFilters] = useState({
    project_id: '',
    user_id: '',
    task_id: '',
    start_date: '',
    end_date: '',
    time_entry_id: time_entry_id || '',
    show_deleted: false,
    show_flagged_only: false,
    show_zero_duration_only: false,
    show_manual_only: false,
    show_timer_only: false
  });

  const [editingEntry, setEditingEntry] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [projectDropdownSearch, setProjectDropdownSearch] = useState('');
  const [taskDropdownSearch, setTaskDropdownSearch] = useState('');
  const [userDropdownSearch, setUserDropdownSearch] = useState('');
  // Project leads see team entries by default; "mine" = only their own.
  const [entryScope, setEntryScope] = useState('all'); // 'all' | 'mine'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    project_id: '',
    task_id: '',
    description: '',
    start_time: '',
    end_time: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [idlePeriods, setIdlePeriods] = useState([]);
  const [currentIdleStart, setCurrentIdleStart] = useState(null);
  const { isIdle } = useIdleDetection(300000); // 5 min default
  const [manualLoggingEnabled] = useState(true);

  const debouncedSearch = useDebounce(searchTerm, 300);

  // Shared filter params for list + stats (no exclusive toggles on stats)
  const baseQueryParams = useMemo(() => {
    const params = {};
    if (filters.project_id) params.project_id = filters.project_id;
    if (filters.task_id) params.task_id = filters.task_id;
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date) params.end_date = filters.end_date;
    if (filters.time_entry_id) params.time_entry_id = filters.time_entry_id;
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.show_deleted) params.include_deleted_refs = 'true';
    if (user?.role === 'project_lead' && entryScope === 'mine') {
      params.scope = 'mine';
    }
    return params;
  }, [
    filters.project_id,
    filters.task_id,
    filters.user_id,
    filters.start_date,
    filters.end_date,
    filters.time_entry_id,
    filters.show_deleted,
    debouncedSearch,
    user?.role,
    entryScope,
  ]);

  const listQueryParams = useMemo(() => {
    const params = {
      ...baseQueryParams,
      page: currentPage,
      page_size: ENTRIES_PER_PAGE,
    };
    if (filters.show_manual_only) params.is_manual = 'true';
    else if (filters.show_timer_only) params.is_manual = 'false';
    if (filters.show_flagged_only && user?.role === 'admin') {
      params.flagged_only = 'true';
    } else if (filters.show_zero_duration_only) {
      params.zero_duration_only = 'true';
    }
    return params;
  }, [baseQueryParams, currentPage, filters.show_manual_only, filters.show_timer_only, filters.show_flagged_only, filters.show_zero_duration_only, user?.role]);

  const statsQueryParams = useMemo(() => {
    // Stats ignore exclusive list toggles so Manual/Timer/Flagged cards stay meaningful
    const { time_entry_id: _omit, ...rest } = baseQueryParams;
    return rest;
  }, [baseQueryParams]);

  const { 
    data: timeEntriesData, 
    isLoading: timeEntriesLoading, 
    isFetching: timeEntriesFetching,
    error: timeEntriesError,
    refetch: fetchTimeEntries
  } = useTimeEntries(listQueryParams);

  const { data: entryStatistics } = useTimeEntryStatistics(statsQueryParams);
  
  const { 
    data: projectsData, 
    isLoading: projectsLoading 
  } = useProjects();
  
  const { 
    data: tasksData, 
    isLoading: tasksLoading 
  } = useTasks(filters.project_id);
  
  const { 
    data: usersData, 
    isLoading: usersLoading 
  } = useUsers();
  
  const createTimeEntryMutation = useCreateTimeEntry();
  const updateTimeEntryMutation = useUpdateTimeEntry();
  const deleteTimeEntryMutation = useDeleteTimeEntry();
  const { data: dailyHoursData } = useDailyHours();
  
  // API already returns the current page
  const timeEntries = useMemo(() => timeEntriesData?.results || [], [timeEntriesData?.results]);
  const serverTotal = timeEntriesData?.total ?? 0;
  const projects = useMemo(() => projectsData?.results || [], [projectsData?.results]);
  const tasks = useMemo(() => tasksData?.results || [], [tasksData?.results]);
  const users = useMemo(() => Array.isArray(usersData) ? usersData : (usersData?.results || []), [usersData]);

  // Only keep useEffect for URL parameter changes
  useEffect(() => {
    if (time_entry_id) {
      setFilters(f => ({ ...f, time_entry_id }));
      setCurrentPage(1);
    }
  }, [time_entry_id]);

  useEffect(() => {
    if (user?.role === 'admin' && showCreateModal) {
      if (isIdle && !currentIdleStart) {
        setCurrentIdleStart(Date.now());
      }
      if (!isIdle && currentIdleStart) {
        setIdlePeriods(prev => [...prev, { start: currentIdleStart, end: Date.now() }]);
        setCurrentIdleStart(null);
      }
    } else {
      setCurrentIdleStart(null);
      setIdlePeriods([]);
    }
  }, [isIdle, showCreateModal, user?.role, currentIdleStart]);

  const formatTime = useCallback((seconds) => {
    // Handle invalid or negative durations
    if (!seconds || seconds < 0) {
      return '0h 0m';
    }
    
   
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
   
    
    return `${hours}h ${minutes}m`;
  }, []);

  const handleEdit = useCallback((entry) => {
    setEditingEntry(entry);

    // Format UTC API timestamps to PKT datetime-local strings so the edit
    // form shows the same wall-clock time users see in the list view.
    const toPKTLocal = (iso) => {
      const d = parseApiDate(iso);
      if (!d) return '';
      // Build YYYY-MM-DDTHH:mm in Asia/Karachi
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Karachi',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      }).formatToParts(d);
      const get = (type) => parts.find(p => p.type === type)?.value || '00';
      return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
    };

    setFormData({
      project_id: entry.project_id,
      task_id: entry.task_id || '',
      description: entry.description,
      start_time: toPKTLocal(entry.start_time),
      end_time: entry.end_time ? toPKTLocal(entry.end_time) : ''
    });
  }, []);

  // Function to check daily hours limit
  const checkDailyHoursLimit = async (startTime, endTime) => {
    try {
      const totalHoursToday = dailyHoursData?.total_hours || 0;
      
      // Calculate duration of new entry
      const start = new Date(startTime);
      const end = new Date(endTime);
      const durationMs = end - start;
      const newEntryHours = durationMs / (1000 * 60 * 60); // Convert to hours
      
      const totalAfterEntry = totalHoursToday + newEntryHours;
      
      if (totalAfterEntry > dailyHoursCap) {
        toast.error(`Daily time limit exceeded. Currently tracking ${totalHoursToday.toFixed(1)} hours today. Adding ${newEntryHours.toFixed(1)} hours would result in ${totalAfterEntry.toFixed(1)} hours total. Maximum allowed is ${dailyHoursCap} hours per day.`);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error checking daily hours:', error);
      return true; // Allow creation if check fails
    }
  };

  const handleSave = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Validate required fields for new entries
      if (!editingEntry) {
        if (!formData.project_id) {
          toast.error('Please select a project');
          return;
        }
        if (!formData.task_id) {
          toast.error('Please select a task');
          return;
        }
        if (!formData.description) {
          toast.error('Please enter a description');
          return;
        }
        
        if (formData.description.trim().length < minDescLen) {
          toast.error(`Description must be at least ${minDescLen} characters long`);
          return;
        }
        if (!formData.start_time) {
          toast.error('Please enter a start time');
          return;
        }
      }

      if (formData.description && formData.description.trim().length < minDescLen) {
        toast.error(`Description must be at least ${minDescLen} characters long`);
        return;
      }

      if (!formData.start_time) {
        toast.error('Please enter a start time');
        return;
      }

      // Block backwards / zero-length ranges (same-day end before start, etc.)
      if (formData.end_time) {
        const startMs = new Date(`${formData.start_time}:00+05:00`).getTime();
        const endMs = new Date(`${formData.end_time}:00+05:00`).getTime();
        if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
          toast.error('Please enter valid start and end times');
          return;
        }
        if (endMs <= startMs) {
          toast.error(
            'End time must be after start time. For overnight work, set the end date to the next day.'
          );
          return;
        }

        // Check daily hours limit for new entries
        if (!editingEntry) {
          const canTrackTime = await checkDailyHoursLimit(formData.start_time, formData.end_time);
          if (!canTrackTime) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      // datetime-local values are in PKT (Asia/Karachi) — convert to UTC
      // ISO strings so the backend stores the correct instant.
      const pktLocalToUTC = (localStr) => {
        if (!localStr) return localStr;
        // Append PKT offset so Date parses the wall-clock as Asia/Karachi
        return new Date(`${localStr}:00+05:00`).toISOString();
      };

      const updateData = {
        description: formData.description,
        start_time: pktLocalToUTC(formData.start_time),
        end_time: pktLocalToUTC(formData.end_time)
      };

      if (editingEntry) {
        await updateTimeEntryMutation.mutateAsync({ id: editingEntry.id, data: updateData });
        toast.success('Time entry updated successfully');
      } else {
        await createTimeEntryMutation.mutateAsync({
          ...updateData,
          project_id: formData.project_id,
            task_id: formData.task_id,
            is_manual: true,
            idle_periods: user?.role === 'admin' ? idlePeriods : []
          });
        toast.success('Time entry created successfully');
      }

      fetchTimeEntries();
      setEditingEntry(null);
      setShowCreateModal(false);
      setFormData({
        project_id: '',
        task_id: '',
        description: '',
        start_time: '',
        end_time: ''
      });
      setIdlePeriods([]);
      setCurrentIdleStart(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save time entry');
    } finally {
      setIsSubmitting(false);
    }
  }, [editingEntry, formData, user?.role, idlePeriods, fetchTimeEntries, checkDailyHoursLimit, createTimeEntryMutation, manualLoggingEnabled, updateTimeEntryMutation, minDescLen]);

  const handleDelete = useCallback((entryId) => {
    setEntryToDelete(entryId);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteTimeEntryMutation.mutateAsync(entryToDelete);
      toast.success('Time entry deleted successfully');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to delete time entry');
    } finally {
      setShowDeleteModal(false);
      setEntryToDelete(null);
      setIsDeleting(false);
    }
  }, [entryToDelete, deleteTimeEntryMutation]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setEntryToDelete(null);
  }, []);

  const getProjectName = useCallback((projectId) => {
    if (!Array.isArray(projects)) return 'Unknown Project';
    const project = projects.find(p => String(p.id) === String(projectId));
    if (!project) return 'Unknown Project';
    return project.name + (project.is_deleted ? ' (deleted)' : '');
  }, [projects]);

  const getTaskName = useCallback((taskId) => {
    if (!Array.isArray(tasks)) return '';
    const task = tasks.find(t => String(t.id) === String(taskId));
    if (!task) return '';
    return task.name + (task.is_deleted ? ' (deleted)' : '');
  }, [tasks]);

  const getUserName = useCallback((userId) => {
    if (!Array.isArray(users)) {
      console.warn('Users array is not available for lookup:', { users, userId });
      return `User ${userId?.slice(0, 8)}...`;
    }
    
    const user = users.find(u => u.id === userId);
    if (user) {
      return user.full_name || user.username || `User ${userId?.slice(0, 8)}...`;
    }
    
    console.warn('User not found in users array:', { userId, usersCount: users.length, availableUserIds: users.map(u => u.id) });
    return `User ${userId?.slice(0, 8)}...`;
  }, [users]);

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      project_id: '',
      user_id: '',
      task_id: '',
      start_date: '',
      end_date: '',
      time_entry_id: '',
      show_deleted: false,
      show_flagged_only: false,
      show_zero_duration_only: false,
      show_manual_only: false,
      show_timer_only: false,
    });
    setSearchTerm('');
    setCurrentPage(1);
  }, []);

  const isEntryFlagged = useCallback((entry) => {
    if (user?.role !== 'admin') return false;
    const flaggedThresholdSeconds = flaggedHours * 3600;
    return (entry.duration || 0) > flaggedThresholdSeconds;
  }, [user?.role, flaggedHours]);

  // Helper function to handle filter toggling (exclusive filters)
  const toggleFilter = useCallback((filterType) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      newFilters.show_flagged_only = false;
      newFilters.show_zero_duration_only = false;
      newFilters.show_manual_only = false;
      newFilters.show_timer_only = false;
      newFilters[filterType] = !prev[filterType];
      return newFilters;
    });
    setCurrentPage(1);
  }, []);

  // Get filtered tasks based on selected project
  const getFilteredTasks = useCallback(() => {
    if (!filters.project_id) return tasks;
    return tasks.filter(task => String(task.project_id) === String(filters.project_id));
  }, [filters.project_id, tasks]);

  // User filter options (full team list — page data alone is not enough)
  const getFilteredUsers = useCallback(() => users, [users]);

  const filteredProjectsForDropdown = useMemo(() => {
    const list = Array.isArray(projects) ? projects : [];
    if (!projectDropdownSearch.trim()) return list;
    const term = projectDropdownSearch.toLowerCase().trim();
    return list.filter(p => (p.name || '').toLowerCase().includes(term));
  }, [projects, projectDropdownSearch]);

  const filteredTasksForDropdown = useMemo(() => {
    const list = getFilteredTasks() || [];
    if (!taskDropdownSearch.trim()) return list;
    const term = taskDropdownSearch.toLowerCase().trim();
    return list.filter(t => (t.name || '').toLowerCase().includes(term));
  }, [getFilteredTasks, taskDropdownSearch]);

  const filteredUsersForDropdown = useMemo(() => {
    const list = getFilteredUsers() || [];
    if (!userDropdownSearch.trim()) return list;
    const term = userDropdownSearch.toLowerCase().trim();
    return list.filter(u =>
      (u.full_name || '').toLowerCase().includes(term) ||
      (u.username || '').toLowerCase().includes(term) ||
      (u.email || '').toLowerCase().includes(term)
    );
  }, [getFilteredUsers, userDropdownSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenFilterDropdown(null);
        setProjectDropdownSearch('');
        setTaskDropdownSearch('');
        setUserDropdownSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openFilterDropdown]);

  const renderFilterDropdown = (type, label, currentValue, displayValue, onSelect, searchValue, onSearchChange, filteredItems) => (
    <div className="w-full relative filter-dropdown">
      <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setOpenFilterDropdown(openFilterDropdown === type ? null : type)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">{displayValue || `All ${label}`}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${openFilterDropdown === type ? 'rotate-180' : ''}`} />
      </button>
      {openFilterDropdown === type && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
          <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                className="input-field w-full !pl-9"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            <button
              type="button"
              onClick={() => { onSelect(''); setOpenFilterDropdown(null); onSearchChange(''); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${!currentValue ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}
            >
              All {label}
            </button>
            {filteredItems.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => { onSelect(item.id); setOpenFilterDropdown(null); onSearchChange(''); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${String(currentValue) === String(item.id) ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}
              >
                {item.name || item.full_name || item.username}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, entryScope]);

  const entryStats = entryStatistics || {};
  const summaryTotal = entryStats.total_entries ?? 0;
  const summaryManual = entryStats.manual_count ?? 0;
  const summaryTimer = entryStats.timer_count ?? 0;
  const summaryFlagged = entryStats.flagged_count ?? 0;
  const summaryZeroDuration = entryStats.zero_duration_count ?? 0;

  // Full-page spinner only on first load (keep list visible while refetching)
  const isLoading = useMemo(() => 
    (timeEntriesLoading && !timeEntriesData) || projectsLoading || tasksLoading || usersLoading, 
    [timeEntriesLoading, timeEntriesData, projectsLoading, tasksLoading, usersLoading]
  );
  
  const totalPages = Math.max(1, Math.ceil(serverTotal / ENTRIES_PER_PAGE));
  const paginatedEntries = timeEntries;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="page-header">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
              {user?.role === 'member'
                ? 'My Time Entries'
                : user?.role === 'project_lead' && entryScope === 'mine'
                  ? 'My Time Entries'
                  : 'Time Entries'}
            </h1>
            {user?.role === 'project_lead' && (
              <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                {entryScope === 'mine'
                  ? 'Entries you tracked'
                  : 'Showing team time entries'}
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {user?.role === 'project_lead' && (
              <div
                className="inline-flex items-center rounded-lg border border-secondary-200 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 p-0.5"
                role="group"
                aria-label="Time entry scope"
              >
                <button
                  type="button"
                  onClick={() => setEntryScope('all')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    entryScope === 'all'
                      ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                  }`}
                >
                  <Users className="h-3.5 w-3.5" />
                  All entries
                </button>
                <button
                  type="button"
                  onClick={() => setEntryScope('mine')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    entryScope === 'mine'
                      ? 'bg-white dark:bg-secondary-700 text-primary-700 dark:text-primary-300 shadow-sm'
                      : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                  }`}
                >
                  <User className="h-3.5 w-3.5" />
                  My entries
                </button>
              </div>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Entry
              </button>
            )}
          </div>
        </div>

        {/* Filter Alert Banners */}
        {(filters.show_flagged_only || filters.show_zero_duration_only || filters.show_manual_only || filters.show_timer_only) && (
          <>
            {filters.show_flagged_only && user?.role === 'admin' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 font-bold text-sm">🚩</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-red-800">
                        Showing Flagged Entries Only
                      </h3>
                      <p className="text-sm text-red-700 mt-1">
                        Displaying {serverTotal} time {serverTotal === 1 ? 'entry' : 'entries'} that exceed {flaggedHours} hours.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilters(prev => ({ ...prev, show_flagged_only: false }));
                      setCurrentPage(1);
                    }}
                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}
            {filters.show_zero_duration_only && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="text-amber-600 font-bold text-sm">0</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-amber-800">
                        Showing 0 min Entries Only
                      </h3>
                      <p className="text-sm text-amber-700 mt-1">
                        Displaying {serverTotal} time {serverTotal === 1 ? 'entry' : 'entries'} with 0 minutes (includes unfinished timers).
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilters(prev => ({ ...prev, show_zero_duration_only: false }));
                      setCurrentPage(1);
                    }}
                    className="text-amber-600 hover:text-amber-800 font-medium text-sm"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}
            {filters.show_manual_only && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-bold text-sm">✍️</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-green-800">
                        Showing Manual Entries Only
                      </h3>
                      <p className="text-sm text-green-700 mt-1">
                        Displaying {serverTotal} manually entered time {serverTotal === 1 ? 'entry' : 'entries'}.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilters(prev => ({ ...prev, show_manual_only: false }));
                      setCurrentPage(1);
                    }}
                    className="text-green-600 hover:text-green-800 font-medium text-sm"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}
            {filters.show_timer_only && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                        <span className="text-purple-600 font-bold text-sm">⏲️</span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-purple-800">
                        Showing Timer Entries Only
                      </h3>
                      <p className="text-sm text-purple-700 mt-1">
                        Displaying {serverTotal} timer-based time {serverTotal === 1 ? 'entry' : 'entries'}.
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilters(prev => ({ ...prev, show_timer_only: false }));
                      setCurrentPage(1);
                    }}
                    className="text-purple-600 hover:text-purple-800 font-medium text-sm"
                  >
                    Show All
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Search Bar */}
        <div className="card">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search by description, project, task, or user..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="card">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
              {renderFilterDropdown(
                'project',
                'Project',
                filters.project_id,
                filters.project_id
                  ? (projects.find(p => String(p.id) === String(filters.project_id))?.name || '')
                  : '',
                (value) => {
                  setFilters(prev => ({ ...prev, project_id: value, task_id: '' }));
                  setCurrentPage(1);
                },
                projectDropdownSearch,
                setProjectDropdownSearch,
                filteredProjectsForDropdown
              )}

              {renderFilterDropdown(
                'task',
                'Task',
                filters.task_id,
                filters.task_id
                  ? (getFilteredTasks().find(t => String(t.id) === String(filters.task_id))?.name || '')
                  : '',
                (value) => handleFilterChange('task_id', value),
                taskDropdownSearch,
                setTaskDropdownSearch,
                filteredTasksForDropdown
              )}

              {(user?.role === 'admin' || user?.role === 'project_lead') && (
                renderFilterDropdown(
                  'user',
                  'User',
                  filters.user_id,
                  filters.user_id
                    ? (() => {
                        const u = getFilteredUsers().find(x => String(x.id) === String(filters.user_id));
                        return u ? (u.full_name || u.username || '') : '';
                      })()
                    : '',
                  (value) => handleFilterChange('user_id', value),
                  userDropdownSearch,
                  setUserDropdownSearch,
                  filteredUsersForDropdown
                )
              )}
             
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  Start Date
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">
                  End Date
                </label>
                <input
                  type="date"
                  className="input-field"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                />
              </div>
            </div>
            {filters.time_entry_id && (
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    setFilters(f => ({ ...f, time_entry_id: '' }));
                    navigate('/time-entries');
                  }}
                  className="btn-secondary"
                >
                  Show All Entries
                </button>
              </div>
            )}
            <div className="flex justify-between items-center mt-4">
              <div className="text-sm text-secondary-600">
                {filters.project_id && (
                  <span className="inline-flex items-center gap-2">
                    <span className="font-medium">Active Project Filter:</span>
                    <span className="bg-primary-100 text-primary-800 px-2 py-1 rounded">
                      {projects.find(p => String(p.id) === String(filters.project_id))?.name || 'Unknown Project'}
                    </span>
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  clearFilters();
                  setOpenFilterDropdown(null);
                  setProjectDropdownSearch('');
                  setTaskDropdownSearch('');
                  setUserDropdownSearch('');
                }}
                className="btn-secondary"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}

        {/* Summary Stats — server totals (not page-capped) */}
        {(summaryTotal > 0 || serverTotal > 0) && (
          <div className="card">
            <h3 className="text-lg font-semibold mb-4">Summary</h3>
            {filters.show_deleted && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="text-blue-800 text-sm">
                  <span className="font-medium">⚠️ Showing deleted entries:</span> Displaying time entries with deleted projects and tasks for audit purposes.
                </div>
              </div>
            )}
            <div className="relative">
              <div className={`grid gap-4 ${user?.role === 'admin' ? 'grid-cols-3 sm:grid-cols-5' : 'grid-cols-2 sm:grid-cols-4'}`}>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600">{summaryTotal}</div>
                  <div className="text-sm text-secondary-600">Entries</div>
                </div>
                <div 
                  className={`text-center cursor-pointer transition-all duration-200 hover:bg-green-50 rounded-lg p-2 ${
                    filters.show_manual_only ? 'bg-green-100 ring-2 ring-green-300' : ''
                  }`}
                  onClick={() => toggleFilter('show_manual_only')}
                  title={filters.show_manual_only ? 'Click to show all entries' : 'Click to show only manual entries'}
                >
                  <div className="text-2xl font-bold text-green-600">
                    {summaryManual}
                  </div>
                  <div className="text-sm text-secondary-600">Manual</div>
                </div>
                <div 
                  className={`text-center cursor-pointer transition-all duration-200 hover:bg-purple-50 rounded-lg p-2 ${
                    filters.show_timer_only ? 'bg-purple-100 ring-2 ring-purple-300' : ''
                  }`}
                  onClick={() => toggleFilter('show_timer_only')}
                  title={filters.show_timer_only ? 'Click to show all entries' : 'Click to show only timer entries'}
                >
                  <div className="text-2xl font-bold text-purple-600">
                    {summaryTimer}
                  </div>
                  <div className="text-sm text-secondary-600">Timer</div>
                </div>
                <div
                  className={`text-center cursor-pointer transition-all duration-200 hover:bg-amber-50 rounded-lg p-2 ${
                    filters.show_zero_duration_only ? 'bg-amber-100 ring-2 ring-amber-300' : ''
                  }`}
                  onClick={() => toggleFilter('show_zero_duration_only')}
                  title={filters.show_zero_duration_only ? 'Click to show all entries' : 'Click to show only 0 min entries'}
                  data-testid="zero-duration-filter-card"
                >
                  <div className="text-2xl font-bold text-amber-600">
                    {summaryZeroDuration}
                  </div>
                  <div className="text-sm text-secondary-600">0 min</div>
                </div>
                {/* Flagged Entries Card - Admin Only */}
                {user?.role === 'admin' && (
                  <div 
                    className={`text-center cursor-pointer transition-all duration-200 hover:bg-red-50 rounded-lg p-2 ${
                      filters.show_flagged_only ? 'bg-red-100 ring-2 ring-red-300' : ''
                    }`}
                    onClick={() => toggleFilter('show_flagged_only')}
                    title={filters.show_flagged_only ? 'Click to show all entries' : 'Click to show only flagged entries'}
                  >
                    <div className="text-2xl font-bold text-red-600">
                      {summaryFlagged}
                    </div>
                    <div className="text-sm text-secondary-600">Flagged</div>
                  </div>
                )}
              </div>
              
            </div>
          </div>
        )}

        {/* Time Entries List */}
        <div className={`card ${timeEntriesFetching ? 'opacity-80' : ''}`}>
          {serverTotal === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-secondary-900 mb-2">
                {searchTerm ? 'No entries match your search' : 'No time entries yet'}
              </h3>
              <p className="text-secondary-600 mb-4">
                {searchTerm ? 'Try adjusting your search terms or filters' : 'Start tracking time or add manual entries'}
              </p>
            </div>
          ) : (
            <>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-secondary-100">
              {Array.isArray(paginatedEntries) && paginatedEntries.map((entry) => {
                let displayName = entry.user_name;
                if (!displayName && entry.user_id) displayName = getUserName(entry.user_id);
                if (!displayName) displayName = `User ${entry.user_id?.slice(0, 8)}...`;
                const canEdit = user?.role === 'admin' || entry.user_id === user?.id;
                return (
                  <div
                    key={entry.id}
                    className={`p-4 space-y-2 ${isEntryFlagged(entry) ? 'bg-red-50 border-l-4 border-red-300' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-secondary-900">
                          {toUTCPlus5(entry.start_time).split(',').slice(0, 2).join(',')}
                        </div>
                        {(user?.role === 'admin' || user?.role === 'project_lead') && (
                          <div className="text-xs text-secondary-500">{displayName}</div>
                        )}
                      </div>
                      <span className="text-sm font-semibold text-primary-600 tabular-nums shrink-0">
                        {formatTime(entry.duration || 0)}
                      </span>
                    </div>
                    <div className="text-sm text-secondary-700 truncate">
                      {entry.project_name || '—'} · {entry.task_name || '—'}
                    </div>
                    {entry.description && (
                      <p className="text-xs text-secondary-500 line-clamp-2">{entry.description}</p>
                    )}
                    {canEdit && (
                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="btn-secondary text-xs min-h-[44px] px-3"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(entry.id)}
                          className="text-xs text-red-600 min-h-[44px] px-3"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-secondary-200">
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Date</th>
                    {(user?.role === 'admin' || user?.role === 'project_lead') && (
                      <th className="text-left py-3 px-4 font-medium text-secondary-700">User</th>
                    )}
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Project</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Task</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700 hidden lg:table-cell">Description</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Duration</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(paginatedEntries) && paginatedEntries.map(entry => (
                    <tr key={entry.id} className={`border-b border-secondary-100 hover:bg-secondary-50 ${
                      isEntryFlagged(entry) 
                        ? 'bg-red-50 hover:bg-red-100 border-red-200' 
                        : ''
                    }`}>
                      <td className="py-3 px-4 text-sm">
                        {toUTCPlus5(entry.start_time).split(',').slice(0,2).join(',')}
                      </td>
                      {(user?.role === 'admin' || user?.role === 'project_lead') && (
                        <td className="py-3 px-4 text-sm">
                          {(() => {
                            // Try to get user name from the entry first, then fallback to lookup
                            let displayName = entry.user_name;
                            
                            if (!displayName && entry.user_id) {
                              displayName = getUserName(entry.user_id);
                            }
                            
                            // If still no name, show user ID as fallback
                            if (!displayName) {
                              displayName = `User ${entry.user_id?.slice(0, 8)}...`;
                            }
                            
                            return displayName;
                          })()}
                        </td>
                      )}
                      <td className="py-3 px-4 text-sm">
                        {entry.project_id ? (
                          (() => {
                            // Use project name from backend response first, then fallback to local lookup
                            const projectName = entry.project_name;
                            if (projectName) {
                              return (
                                <a
                                  href={`/projects/${entry.project_id}`}
                                  className="text-secondary-700 hover:text-secondary-900 hover:underline font-medium transition-colors"
                                  title={`View ${projectName} project`}
                                >
                                  {projectName}
                                </a>
                              );
                            }
                            
                            // Fallback to local project lookup
                            const project = projects.find(p => String(p.id) === String(entry.project_id));
                            if (!project) {
                              return <span className="text-red-600 italic">Project Deleted</span>;
                            }
                            if (project.is_deleted) {
                              return <span className="text-red-600 italic">{project.name} (deleted)</span>;
                            }
                            return (
                              <a
                                href={`/projects/${entry.project_id}`}
                                className="text-secondary-700 hover:text-secondary-900 hover:underline font-medium transition-colors"
                                title={`View ${project.name} project`}
                              >
                                {project.name}
                              </a>
                            );
                          })()
                        ) : (
                          'No Project'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {entry.task_id ? (
                          (() => {
                            // Use task name from backend response first, then fallback to local lookup
                            const taskName = entry.task_name;
                            if (taskName) {
                              return (
                                <a
                                  href={`/tasks/${entry.task_id}`}
                                  className="text-secondary-700 hover:text-secondary-900 hover:underline font-medium transition-colors"
                                  title={`View ${taskName} task`}
                                >
                                  {taskName}
                                </a>
                              );
                            }
                            
                            // Fallback to local task lookup
                            const task = tasks.find(t => String(t.id) === String(entry.task_id));
                            if (!task) {
                              return <span className="text-red-600 italic">Task Deleted</span>;
                            }
                            if (task.is_deleted) {
                              return <span className="text-red-600 italic">{task.name} (deleted)</span>;
                            }
                            return (
                              <a
                                href={`/tasks/${entry.task_id}`}
                                className="text-secondary-700 hover:text-secondary-900 hover:underline font-medium transition-colors"
                                title={`View ${task.name} task`}
                              >
                                {task.name}
                              </a>
                            );
                          })()
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm hidden lg:table-cell">{entry.description}</td>
                      <td className="py-3 px-4 text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <span className={isEntryFlagged(entry) ? 'text-red-600 font-bold' : ''}>
                            {(() => {
                              // Debug logging for duration issues
                              if (entry.duration < 0) {
                                console.warn('Negative duration detected:', {
                                  entryId: entry.id,
                                  duration: entry.duration,
                                  startTime: entry.start_time,
                                  endTime: entry.end_time,
                                  projectName: entry.project_name,
                                  taskName: entry.task_name
                                });
                              }
                              
                             
                              
                              return formatTime(entry.duration || 0);
                            })()}
                          </span>
                          {isEntryFlagged(entry) && (
                            <span className="text-red-500 text-xs" title={`Time entry exceeds ${flaggedHours} hours`}>
                              🚩
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-2">
                          {/* Show edit button only for own entries (members) or all entries (admins) */}
                          {(user?.role === 'admin' || entry.user_id === user?.id) && (
                            <button
                              onClick={() => handleEdit(entry)}
                              className="p-1 text-secondary-400 hover:text-primary-600 transition-colors"
                              title="Edit Entry"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Show delete button only for own entries (members/project leads) or all entries (admins) */}
                          {(user?.role === 'admin' || entry.user_id === user?.id) && (
                            <button
                              onClick={() => handleDelete(entry.id)}
                              className="p-1 text-secondary-400 hover:text-red-600 transition-colors"
                              title="Delete Entry"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          
                          {/* Show info for project leads when they can't edit/delete */}
                          {user?.role === 'project_lead' && entry.user_id !== user?.id && (
                            <span className="text-xs text-secondary-500 italic">
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
          
          {/* Pagination Controls */}
          {serverTotal > ENTRIES_PER_PAGE && (
            <div className="mt-6">
              {/* Page Info */}
              <div className="flex justify-center items-center mb-4 text-sm text-secondary-600">
                <span>
                  Showing {((currentPage - 1) * ENTRIES_PER_PAGE) + 1} to {Math.min(currentPage * ENTRIES_PER_PAGE, serverTotal)} of {serverTotal} entries
                </span>
              </div>
              
              {/* Pagination Navigation */}
              <div className="flex justify-center items-center gap-2">
                {/* Previous Button */}
                <button 
                  className="btn-secondary px-3 py-2 flex items-center gap-1" 
                  disabled={currentPage === 1} 
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                
                {/* Page Numbers */}
                {(() => {
                  const maxVisiblePages = 10;
                  const pages = [];
                  
                  if (totalPages <= maxVisiblePages) {
                    // Show all pages if total is 10 or less
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`px-3 py-2 rounded transition-colors ${
                            currentPage === i 
                              ? 'bg-primary-600 text-white' 
                              : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                          }`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      );
                    }
                  } else {
                    // Smart pagination for more than 10 pages
                    const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    // Always show first page
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          className="px-3 py-2 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                          onClick={() => setCurrentPage(1)}
                        >
                          1
                        </button>
                      );
                      
                      // Add ellipsis if there's a gap
                      if (startPage > 2) {
                        pages.push(
                          <span key="ellipsis1" className="px-2 text-secondary-500">
                            ...
                          </span>
                        );
                      }
                    }
                    
                    // Show visible page range
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          className={`px-3 py-2 rounded transition-colors ${
                            currentPage === i 
                              ? 'bg-primary-600 text-white' 
                              : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                          }`}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      );
                    }
                    
                    // Always show last page
                    if (endPage < totalPages) {
                      // Add ellipsis if there's a gap
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span key="ellipsis2" className="px-2 text-secondary-500">
                            ...
                          </span>
                        );
                      }
                      
                      pages.push(
                        <button
                          key={totalPages}
                          className="px-3 py-2 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                          onClick={() => setCurrentPage(totalPages)}
                        >
                          {totalPages}
                        </button>
                      );
                    }
                  }
                  
                  return pages;
                })()}
                
                {/* Next Button */}
                <button 
                  className="btn-secondary px-3 py-2 flex items-center gap-1" 
                  disabled={currentPage === totalPages} 
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Time Entry Drawer */}
      <FormDrawer
        open={Boolean(editingEntry) || showCreateModal}
        onClose={() => { setEditingEntry(null); setShowCreateModal(false); }}
        title={editingEntry ? 'Edit Time Entry' : 'Create Time Entry'}
        subtitle={editingEntry ? 'Update the details of this time entry.' : 'Manually log work for a task on a project.'}
        icon={<Clock className="h-5 w-5" />}
        iconColor="primary"
        footer={(
          <>
            <button
              type="button"
              onClick={() => { setEditingEntry(null); setShowCreateModal(false); }}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="btn-primary flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {editingEntry ? 'Updating…' : 'Creating…'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingEntry ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </>
        )}
      >
            <div className="space-y-6">
              {!editingEntry && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Project *
                    </label>
                    <select
                      className="input-field w-full"
                      value={formData.project_id}
                      onChange={(e) => setFormData({...formData, project_id: e.target.value})}
                      required
                    >
                      <option value="">Select a project</option>
                      {Array.isArray(projects) && projects.map(project => (
                        <option key={project.id} value={project.id}>{project.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Task *
                    </label>
                    <select
                      className="input-field w-full"
                      value={formData.task_id}
                      onChange={(e) => setFormData({...formData, task_id: e.target.value})}
                      required
                    >
                      <option value="">Select a task</option>
                      {Array.isArray(tasks) && tasks.filter(task => task.project_id === formData.project_id).map(task => (
                        <option key={task.id} value={task.id}>{task.name}</option>
                      ))}
                    </select>
                    {formData.project_id && (
                      <p className="text-xs text-secondary-500 mt-1">Showing tasks for selected project</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Description <span className="text-red-500 text-xs font-normal">*</span>
                  <span className="text-xs text-gray-500 font-normal ml-1">(min. {minDescLen} characters)</span>
                </label>
                <input
                  type="text"
                  className="input-field w-full"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Please provide detailed description of your work..."
                  required
                />
                <div className="flex justify-between items-center mt-1">
                  <span className={`text-xs font-medium ${formData.description.length < minDescLen ? 'text-red-500' : 'text-green-600'}`}>
                    {formData.description.length}/{minDescLen}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Start Time *
                  </label>
                  <input
                    type="datetime-local"
                    className="input-field w-full"
                    value={formData.start_time}
                    onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    End Time
                  </label>
                  <input
                    type="datetime-local"
                    className="input-field w-full"
                    value={formData.end_time}
                    min={formData.start_time || undefined}
                    onChange={(e) => setFormData({...formData, end_time: e.target.value})}
                  />
                  {formData.start_time && formData.end_time && (
                    new Date(`${formData.end_time}:00+05:00`) <= new Date(`${formData.start_time}:00+05:00`)
                  ) && (
                    <p className="text-xs text-red-600 mt-1">
                      End time must be after start time. For overnight work, use the next day.
                    </p>
                  )}
                </div>
              </div>

            </div>
      </FormDrawer>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={showDeleteModal}
        title="Delete Time Entry"
        message="Are you sure you want to delete this time entry? This action cannot be undone."
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonStyle="danger"
        isLoading={isDeleting}
      />
    </>
  );
};

export default TimeEntriesPage;